/**
 * useScanToFill — Capture image → OCR → classify → form initialValues (DRR-159)
 *
 * Reusable hook that wires the full intake intelligence pipeline into any form:
 * 1. Accept image Blob (from camera, file picker, wearable, etc.)
 * 2. Device OCR extracts text on-device (T4 stays local)
 * 3. classifyIntakeText routes to entity type + extracts field values
 * 4. Returns initialValues compatible with EntityFormGenerator / FormRenderer
 *
 * FERP Principles:
 * - DRY: Single hook for all scan-to-fill use cases (sidebar, modal, full-page)
 * - DRR-114: T4 raw media processed on-device, never uploaded for OCR
 * - DRR-128: Write-time minimization — only structured text leaves device
 * - Dumb Frontend: classification logic lives in backend IntakeDispatcher
 * - Repository pattern: all data access via GraphQL mutation
 */

import { useCallback, useState } from 'react';
import { useDeviceOcr } from './useDeviceOcr';
import {
  useIntakeClassification,
  type IntakeClassification,
} from './useIntakeClassification';

// ── Types ────────────────────────────────────────────────────────────────────

export type ScanStatus = 'idle' | 'capturing' | 'processing' | 'done';

export interface ScanToFillResult {
  /** Pre-fill values for EntityFormGenerator initialValues prop */
  initialValues: Record<string, unknown> | undefined;
  /** Human-readable status message */
  statusMessage: string | null;
  /** Current scan state */
  status: ScanStatus;
  /** Whether scan is in progress (capturing, OCR, or classifying) */
  scanning: boolean;
  /** Whether device-native OCR is available */
  ocrAvailable: boolean;
  /** Last classification result (null if no scan or classification failed) */
  classification: IntakeClassification | null;
  /** Process an image blob through the OCR → classify → fill pipeline */
  processImage: (imageBlob: Blob) => Promise<Record<string, unknown> | undefined>;
  /** Reset to idle state */
  reset: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useScanToFill(): ScanToFillResult {
  const [initialValues, setInitialValues] = useState<Record<string, unknown> | undefined>(undefined);
  const [status, setStatus] = useState<ScanStatus>('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const { extractText, processing: ocrProcessing, isAvailable: ocrAvailable } = useDeviceOcr();
  const { classify, classification, loading: classifying, reset: resetClassification } = useIntakeClassification();

  const scanning = status === 'capturing' || status === 'processing' || ocrProcessing || classifying;

  const processImage = useCallback(
    async (imageBlob: Blob): Promise<Record<string, unknown> | undefined> => {
      setStatus('processing');
      setStatusMessage('Extracting text...');

      // 1. Device OCR
      const ocrResult = await extractText(imageBlob);
      if (!ocrResult || !ocrResult.text.trim()) {
        setStatusMessage('No text detected. Try again with a clearer image.');
        setStatus('idle');
        return undefined;
      }

      setStatusMessage(`${ocrResult.blockCount} text blocks found. Classifying...`);

      // 2. Classify via backend IntakeDispatcher
      const classificationResult = await classify(ocrResult.text);

      if (!classificationResult) {
        // Classification failed but we have raw text — use it as a name/description fallback
        const fallbackValues = { name: ocrResult.text.slice(0, 100), description: ocrResult.text };
        setInitialValues(fallbackValues);
        setStatusMessage('Classification unavailable — raw text applied');
        setStatus('done');
        return fallbackValues;
      }

      // 3. Convert extracted fields to initialValues
      const values: Record<string, unknown> = {};
      for (const field of classificationResult.extractedFields) {
        values[field.fieldName] = field.value;
      }

      setInitialValues(values);
      setStatusMessage(
        `${classificationResult.displayName} (${Math.round(classificationResult.confidence * 100)}% confidence, ${classificationResult.extractedFields.length} fields)`,
      );
      setStatus('done');
      return values;
    },
    [extractText, classify],
  );

  const reset = useCallback(() => {
    setInitialValues(undefined);
    setStatus('idle');
    setStatusMessage(null);
    resetClassification();
  }, [resetClassification]);

  return {
    initialValues,
    statusMessage,
    status,
    scanning,
    ocrAvailable,
    classification,
    processImage,
    reset,
  };
}
